/*globals $ */

import { expoOut, expoInOut } from 'eases';
import pulseTimer from './pulseTimer';

class PhilosophySp {
  constructor(pageApp) {
    document.body.style.overflowY = 'hidden';

    this._pageApp = pageApp;
    this._el = document.querySelector('.PhilosophySp');
    this._el.classList.add('PhilosophySp--init');
    new PhilosophySpPages(this._el.querySelector('.PhilosophySpPages'));
    this._overlay = new PhilosophySpOverlay(
      this._el.querySelector('.PhilosophySpOverlay')
    );

    this._openOverlay = this._openOverlay.bind(this);

    this._el.addEventListener('click', this._openOverlay);
  }

  _openOverlay(e) {
    if (e.target.tagName === 'A' && 'itemIndex' in e.target.dataset) {
      this._overlay.open(parseInt(e.target.dataset.itemIndex));
    }
  }
}

export default PhilosophySp;

class PhilosophySpPages {
  get position() {
    return this._position;
  }
  set position(value) {
    if (this._position !== value) {
      this._position = value;
      this._updatePosition();
    }
  }

  get index() {
    return this._index;
  }
  set index(value) {
    if (this._index !== value) {
      this._index = value;
      this._updateIndex();
    }
  }

  constructor(el) {
    this._el = el;
    this._vpages = [...el.getElementsByClassName('PhilosophySpPages__vpage')];
    new PhilosophySpHero(el.querySelector('.PhilosophySpHero'));
    this._statement = new PhilosophySpStatement(
      el.querySelector('.PhilosophySpStatement')
    );
    this._chapters = [...el.getElementsByClassName('PhilosophySpChapter')].map(
      chapter => new PhilosophySpChapter(chapter)
    );
    this._positionNotifyees = [this._statement, ...this._chapters];

    this._index = 0;
    this._position = 0;
    this._startPosition = 0;
    this._startXY = [0, 0];
    this._lastXY = [0, 0];
    this._currXY = [0, 0];
    this._timer = null;

    this._onDown = this._onDown.bind(this);
    this._onFirstMove = this._onFirstMove.bind(this);
    this._onMove = this._onMove.bind(this);
    this._onUp = this._onUp.bind(this);
    this._updateSize = this._updateSize.bind(this);
    this._onTopAnchorClick = this._onTopAnchorClick.bind(this);

    this._el.addEventListener('touchstart', this._onDown);
    this._el.addEventListener('click', this._onTopAnchorClick);
    window.addEventListener('resize', this._updateSize);
    this._updateSize();
    this._updatePosition();
  }

  // ジェスチャ関連
  // ===

  _onDown(e) {
    this._el.addEventListener('touchmove', this._onFirstMove);
    this._el.addEventListener('touchend', this._onUp);
    this._el.addEventListener('touchcancel', this._onUp);

    this._startPosition = this._position;
    this._startXY = [e.touches[0].clientX, e.touches[0].clientY];
    this._lastXY = [...this._startXY];
    this._currXY = [...this._startXY];

    if (this._timer) {
      this._timer();
      this._timer = null;
    }
  }

  _onFirstMove(e) {
    this._el.removeEventListener('touchmove', this._onFirstMove);

    const x = e.touches[0].clientX;
    const y = e.touches[0].clientY;
    this._currXY = [x, x];
    const [lastX, lastY] = this._lastXY;

    const distX = Math.abs(x - lastX);
    const distY = Math.abs(y - lastY);

    if (distY > distX) {
      this._el.removeEventListener('touchmove', this._onFirstMove);
      this._el.addEventListener('touchmove', this._onMove);
      this._onMove(e);
    } else {
      this._el.removeEventListener('touchmove', this._onFirstMove);
      this._el.removeEventListener('touchend', this._onUp);
      this._el.removeEventListener('touchcancel', this._onUp);
    }
  }

  _onMove(e) {
    this._lastXY = [...this._currXY];
    this._currXY = [e.touches[0].clientX, e.touches[0].clientY];

    const y = e.touches[0].clientY;
    const startY = this._startXY[1];
    const offsetY = y - startY;
    const offsetPosition = (offsetY / this._el.clientHeight) * -1;

    this._position = this._startPosition + offsetPosition;
    this._updatePosition();
  }

  _onUp() {
    this._el.removeEventListener('touchmove', this._onFirstMove);
    this._el.removeEventListener('touchmove', this._onMove);
    this._el.removeEventListener('touchend', this._onUp);
    this._el.removeEventListener('touchcancel', this._onUp);

    const roundPosition = Math.round(this._position);
    const minPosition = Math.max(Math.round(this._startPosition) - 1, 0);
    const maxPosition = Math.min(
      Math.round(this._startPosition) + 1,
      this._vpages.length - 1
    );

    const deltaY = this._currXY[1] - this._lastXY[1];
    let destPosition = roundPosition;
    if (deltaY > 2) {
      destPosition -= 1;
    } else if (deltaY < -2) {
      destPosition += 1;
    }

    destPosition = Math.max(destPosition, minPosition);
    destPosition = Math.min(destPosition, maxPosition);

    const basePosition = this._position;
    const positionDelta = destPosition - basePosition;

    this._timer = pulseTimer(500, progress => {
      if (progress >= 1) {
        this._position = destPosition;
        this._updatePosition();
        this._timer = null;
      } else {
        this._position = basePosition + positionDelta * expoOut(progress);
        this._updatePosition();
      }
    });
  }

  // サイズ調整
  // ===

  _updateSize() {
    const height = window.innerHeight - 60;
    this._el.style.height = `${height}px`;
  }

  // ポジション（スクロール位置）コントロール
  // ===

  _updatePosition() {
    const elHeight = this._el.clientHeight;
    let bouncedPosition = this._position;
    const bottomEdge = this._vpages.length - 1;
    if (this._position < 0) {
      bouncedPosition = this._position * 0.25;
    } else if (this._position > bottomEdge) {
      bouncedPosition = bottomEdge + (this._position - bottomEdge) * 0.25;
    }

    this._vpages.forEach((vpage, i) => {
      let y = i * elHeight;
      y += bouncedPosition * elHeight * -1;
      vpage.style.transform = `translate3d(0, ${y}px, 0)`;
    });

    let index = Math.round(this._position);
    index = Math.max(index, 0);
    index = Math.min(index, this._vpages.length - 1);
    this.index = index;
  }

  // ---

  _onTopAnchorClick(e) {
    if (e.target.tagName === 'A' && e.target.getAttribute('href') === '#top') {
      e.preventDefault();
      this._scrollToTop();
    }
  }

  // ---

  _scrollToTop() {
    if (this._timer) {
      this._timer();
    }

    const basePosition = this._position;
    const destPosition = 0;
    const positionDelta = destPosition - basePosition;

    this._timer = pulseTimer(1000, progress => {
      if (progress >= 1) {
        this._position = destPosition;
        this._updatePosition();
        this._timer = null;
      } else {
        this._position = basePosition + positionDelta * expoInOut(progress);
        this._updatePosition();
      }
    });
  }

  // ---

  _updateIndex() {
    const vpage = this._vpages[this._index];
    this._positionNotifyees.forEach(notifyee => {
      if (vpage.contains(notifyee.el)) {
        notifyee.onPosition();
      }
    });
  }
}

class PhilosophySpHero {
  constructor(el) {
    this._el = el;
    this._img = el.querySelector('img');

    setTimeout(() => {
      this._el.classList.add('PhilosophySpHero--init');
    }, 0);

    this._onImageLoad = this._onImageLoad.bind(this);

    this._img.addEventListener('load', this._onImageLoad);
    setTimeout(this._onImageLoad, 0);
  }

  _onImageLoad() {
    if (this._img.complete) {
      this._img.classList.add('-loaded');
    }
  }
}

class PhilosophySpStatement {
  get el() {
    return this._el;
  }

  constructor(el) {
    this._el = el;
    this._inner = el.querySelector('.PhilosophySpStatement__inner');
    this._remoteTarget = document.getElementById(el.dataset.remoteTarget);

    this._initialSize = parseInt(el.dataset.size);
    this._updateSize = this._updateSize.bind(this);

    window.addEventListener('resize', this._updateSize);
    this._updateSize();
  }

  onPosition() {
    this._el.classList.add('PhilosophySpStatement--init');
  }

  _updateSize() {
    if (!this._el.clientHeight) {
      setTimeout(this._updateSize, 0);
      return;
    }
    let size = this._initialSize;
    this._el.dataset.size = size;
    this._remoteTarget.dataset.size = size;

    while (this._inner.offsetHeight > this._el.clientHeight && size > 0) {
      size--;
      this._el.dataset.size = size;
      this._remoteTarget.dataset.size = size;
    }
  }
}

class PhilosophySpChapter {
  get el() {
    return this._el;
  }

  constructor(el) {
    this._el = el;
    this._indicators = el.querySelectorAll(
      '.PhilosophySpChapter__header button'
    );
    this._hslick = el.querySelector('.PhilosophySpChapter__hslick');
    $(this._hslick).slick({
      arrows: false,
      infinite: false
    });

    this._onSlickChange = this._onSlickChange.bind(this);

    $(this._hslick).on('afterChange', this._onSlickChange);
    this._onSlickChange();
  }

  onPosition() {
    if (this._el.classList.contains('PhilosophySpChapter--init')) {
      return;
    }
    this._el.classList.add('PhilosophySpChapter--init');
    [...this._hslick.getElementsByTagName('li')].forEach(li => {
      const delay = Math.random() * 0.5;
      li.style.transitionDelay = `${delay}s`;
    });
  }

  _onSlickChange() {
    const index = $(this._hslick).slick('slickCurrentSlide');
    this._indicators.forEach((indicator, i) => {
      if (i === index) {
        indicator.classList.add('-selected');
      } else {
        indicator.classList.remove('-selected');
      }
    });
  }
}

class PhilosophySpOverlay {
  constructor(el) {
    this._el = el;
    this._closeButton = el.querySelector('.PhilosophySpOverlay__close-button');
    this._slick = el.querySelector('.PhilosophySpOverlay__slick');
    $(this._slick).slick({
      infinite: false
    });

    this._close = this._close.bind(this);

    this._closeButton.addEventListener('click', this._close);
  }

  open(index = 0) {
    $(this._slick).slick('slickGoTo', index, true);
    this._el.classList.add('PhilosophySpOverlay--opened');
  }

  _close() {
    this._el.classList.remove('PhilosophySpOverlay--opened');
  }
}
